import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { pagos } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  GripVertical,
  Calendar,
  AlertTriangle,
  Loader2,
  DollarSign,
  ChevronRight,
  Trash2,
  Repeat
} from 'lucide-react';
import { Button } from '../components/ui/button';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const PRIORITY_CONFIG = {
  1: { label: 'Urgente', shortLabel: 'P1', color: '#EF4444', bgColor: 'bg-red-50', borderColor: 'border-red-300' },
  2: { label: 'Alta', shortLabel: 'P2', color: '#F59E0B', bgColor: 'bg-orange-50', borderColor: 'border-orange-300' },
  3: { label: 'Media', shortLabel: 'P3', color: '#FBBF24', bgColor: 'bg-yellow-50', borderColor: 'border-yellow-300' },
  4: { label: 'Baja', shortLabel: 'P4', color: '#10B981', bgColor: 'bg-green-50', borderColor: 'border-green-300' }
};

// Desktop Kanban Card
const PagoCard = ({ pago, onDragStart, onDragEnd, onDelete }) => {
  const isVencido = pago.estatus === 'vencido';
  const isRecurrente = pago.tipo_recurrencia === 'mensual';

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, pago)}
      onDragEnd={onDragEnd}
      className={`kanban-card ${isVencido ? 'ring-2 ring-red-400' : ''}`}
      data-testid={`pago-card-${pago.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-slate-400 cursor-grab" />
          <span className="font-medium text-sm text-slate-900">{pago.proveedor}</span>
          {isRecurrente && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">
              <Repeat className="w-2.5 h-2.5" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isVencido && <AlertTriangle className="w-4 h-4 text-red-500" />}
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(pago); }}
            className="p-1 hover:bg-red-100 rounded text-slate-400 hover:text-red-500 transition-colors"
            title="Eliminar pago"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      
      <p className="text-xs text-slate-500 mb-3 line-clamp-2">
        {pago.concepto || 'Sin descripción'}
      </p>
      
      <div className="flex items-center justify-between">
        <span className="font-bold text-sm tabular-nums">{formatCurrency(pago.monto)}</span>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <Calendar className="w-3 h-3" />
          {formatDate(pago.fecha_vencimiento)}
        </div>
      </div>
    </div>
  );
};

// Desktop Kanban Column
const KanbanColumn = ({ priority, pagosList, onDrop, onDragOver, onDragStart, onDragEnd, onDelete }) => {
  const config = PRIORITY_CONFIG[priority];
  const columnPagos = pagosList.filter(p => p.prioridad === priority && p.estatus !== 'pagado');
  const total = columnPagos.reduce((sum, p) => sum + p.monto, 0);

  return (
    <div
      className={`kanban-column ${config.bgColor} ${config.borderColor}`}
      onDrop={(e) => onDrop(e, priority)}
      onDragOver={onDragOver}
    >
      <div className="kanban-column-header" style={{ borderColor: config.color }}>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
          <span className="font-semibold text-sm text-slate-700">{config.label}</span>
          <Badge variant="secondary" className="text-xs">{columnPagos.length}</Badge>
        </div>
        <span className="text-xs font-medium tabular-nums text-slate-500">{formatCurrency(total)}</span>
      </div>
      
      <div className="space-y-2">
        {columnPagos.map((pago) => (
          <PagoCard
            key={pago.id}
            pago={pago}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDelete={onDelete}
          />
        ))}
        {columnPagos.length === 0 && (
          <div className="text-center py-8 text-slate-400 text-sm">
            Sin pagos
          </div>
        )}
      </div>
    </div>
  );
};

// Mobile Pago Card (bigger touch targets)
const MobilePagoCard = ({ pago, onPriorityChange, onDelete }) => {
  const isVencido = pago.estatus === 'vencido';
  const config = PRIORITY_CONFIG[pago.prioridad];
  const isRecurrente = pago.tipo_recurrencia === 'mensual';

  return (
    <div className={`mobile-card ${isVencido ? 'border-red-300 bg-red-50/50' : ''}`}>
      <div className="mobile-card-header">
        <div>
          <div className="mobile-card-title flex items-center gap-2">
            {pago.proveedor}
            {isRecurrente && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">
                <Repeat className="w-3 h-3" />
                Mensual
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1">{pago.concepto || 'Sin descripción'}</p>
        </div>
        <div className="flex items-center gap-2">
          {isVencido && (
            <Badge variant="destructive" className="text-xs">Vencido</Badge>
          )}
          <button
            onClick={() => onDelete(pago)}
            className="p-2 hover:bg-red-100 rounded text-slate-400 hover:text-red-500 transition-colors"
            title="Eliminar pago"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      <div className="mobile-card-row">
        <label>Monto</label>
        <span className="font-bold text-slate-900">{formatCurrency(pago.monto)}</span>
      </div>
      
      <div className="mobile-card-row">
        <label>Vencimiento</label>
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3 text-slate-400" />
          {formatDate(pago.fecha_vencimiento)}
        </span>
      </div>
      
      <div className="mt-3 pt-3 border-t border-slate-100">
        <label className="text-xs text-slate-500 mb-2 block">Cambiar prioridad:</label>
        <div className="flex gap-2">
          {[1, 2, 3, 4].map((p) => (
            <button
              key={p}
              onClick={() => onPriorityChange(pago.id, p)}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                pago.prioridad === p 
                  ? 'text-white shadow-sm' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              style={pago.prioridad === p ? { backgroundColor: PRIORITY_CONFIG[p].color } : {}}
            >
              {PRIORITY_CONFIG[p].shortLabel}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function Prioridades() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pagosList, setPagosList] = useState([]);
  const [draggedPago, setDraggedPago] = useState(null);
  const [activeMobileTab, setActiveMobileTab] = useState(1);
  
  // Current period (month)
  const currentPeriodo = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    loadPagos();
  }, []);

  const loadPagos = async () => {
    setLoading(true);
    try {
      // First generate recurring payments for current period
      try {
        await pagos.generarRecurrentes(currentPeriodo);
      } catch (e) {
        console.log('Recurring generation skipped');
      }
      
      // Load only payments for current period
      const response = await pagos.getAll({ periodo: currentPeriodo });
      setPagosList(response.data.filter(p => p.estatus !== 'pagado'));
    } catch (error) {
      toast.error('Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, pago) => {
    setDraggedPago(pago);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDraggedPago(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newPriority) => {
    e.preventDefault();
    if (!draggedPago || draggedPago.prioridad === newPriority) {
      setDraggedPago(null);
      return;
    }
    await updatePriority(draggedPago.id, newPriority);
    setDraggedPago(null);
  };

  const updatePriority = async (pagoId, newPriority) => {
    try {
      await pagos.updatePriority(pagoId, newPriority);
      setPagosList(prev => 
        prev.map(p => p.id === pagoId ? { ...p, prioridad: newPriority } : p)
      );
      toast.success(`Prioridad actualizada a ${PRIORITY_CONFIG[newPriority].label}`);
    } catch (error) {
      toast.error('Error al actualizar prioridad');
    }
  };

  const handleDelete = async (pago) => {
    const isRecurrente = pago.tipo_recurrencia === 'mensual';
    const confirmMsg = isRecurrente 
      ? `¿Eliminar el pago recurrente "${pago.proveedor}"? Se eliminará solo este mes, el pago seguirá generándose los próximos meses.`
      : `¿Eliminar el pago "${pago.proveedor}"?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await pagos.delete(pago.id);
      setPagosList(prev => prev.filter(p => p.id !== pago.id));
      toast.success('Pago eliminado');
    } catch (error) {
      toast.error('Error al eliminar el pago');
    }
  };

  // Calculate totals
  const totals = [1, 2, 3, 4].reduce((acc, p) => {
    const items = pagosList.filter(pago => pago.prioridad === p);
    acc[p] = { count: items.length, total: items.reduce((s, i) => s + i.monto, 0) };
    return acc;
  }, {});

  const grandTotal = pagosList.reduce((s, p) => s + p.monto, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="prioridades-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>Prioridades de Pago</h1>
            <p>Organiza tus pagos por nivel de urgencia</p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Total pendiente:</span>
            <span className="font-bold text-slate-900 tabular-nums">{formatCurrency(grandTotal)}</span>
          </div>
        </div>
      </div>

      <div className="page-content">
        {/* Mobile View - Tabs */}
        <div className="mobile-only">
          {/* Priority Tabs */}
          <div className="kanban-mobile-tabs">
            {[1, 2, 3, 4].map((p) => (
              <button
                key={p}
                onClick={() => setActiveMobileTab(p)}
                className={`kanban-mobile-tab ${activeMobileTab === p ? 'active' : ''}`}
                style={activeMobileTab === p ? { backgroundColor: PRIORITY_CONFIG[p].color, borderColor: PRIORITY_CONFIG[p].color } : {}}
              >
                {PRIORITY_CONFIG[p].label}
                <span className="count">{totals[p].count}</span>
              </button>
            ))}
          </div>

          {/* Total for active tab */}
          <div className="mb-4 p-3 bg-slate-100 rounded-lg flex justify-between items-center">
            <span className="text-sm text-slate-600">Total {PRIORITY_CONFIG[activeMobileTab].label}</span>
            <span className="font-bold tabular-nums">{formatCurrency(totals[activeMobileTab].total)}</span>
          </div>

          {/* Cards for active priority */}
          <div className="mobile-card-list">
            {pagosList
              .filter(p => p.prioridad === activeMobileTab)
              .map(pago => (
                <MobilePagoCard 
                  key={pago.id} 
                  pago={pago} 
                  onPriorityChange={updatePriority}
                  onDelete={handleDelete}
                />
              ))}
            {pagosList.filter(p => p.prioridad === activeMobileTab).length === 0 && (
              <div className="empty-state">
                <div className="empty-state-icon">✓</div>
                <div className="empty-state-title">Sin pagos en esta prioridad</div>
                <div className="empty-state-text">Los pagos aparecerán aquí cuando los asignes a {PRIORITY_CONFIG[activeMobileTab].label}</div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop View - Kanban Board */}
        <div className="desktop-only">
          <div className="kanban-board">
            {[1, 2, 3, 4].map((priority) => (
              <KanbanColumn
                key={priority}
                priority={priority}
                pagosList={pagosList}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>

        {/* Help tip */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg text-center text-blue-700 text-sm">
          💡 {window.innerWidth < 768 
            ? 'Toca los botones P1-P4 para cambiar la prioridad de un pago' 
            : 'Arrastra los pagos entre columnas para cambiar su prioridad'}
        </div>
      </div>
    </div>
  );
}
